import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { z } from 'zod';
import {
  Alert,
  AttachmentField,
  Button,
  Card,
  cn,
  DataList,
  DataRow,
  DateField,
  Field,
  Filters,
  formatBytes,
  Icon,
  Kanban,
  KanbanCardBody,
  KanbanStagesModal,
  KanbanTag,
  Modal,
  ModalActions,
  NotProvided,
  Page,
  PageHeader,
  Select,
  Table,
  TextArea,
  Toolbar,
  linkTextClasses,
  readAttachmentContent,
  useAttachments,
  type FilterValues,
  type KanbanMove,
  type KanbanStage,
  type KanbanStageActions,
  type StageEventsConfig,
} from '@evogard/ui';
import {
  AWARD_DEFAULT_PAYMENT_METHOD,
  EXPENSE_PAYMENT_METHODS,
  EXPENSE_PAYMENT_METHOD_LABELS,
  LATE_SEND_LABEL,
  PAYMENT_ACT_ACTIONS,
  PAYMENT_STAGE_EVENT_LABELS,
  PAYMENT_STAGE_EVENT_TYPES,
  PAYMENT_STATUS_LABELS,
  actFitsLot,
  boardAct,
  businessDate,
  format,
  formatDate,
  formatInstant,
  fromColumn,
  loadFailureMessage,
  paymentTransition,
  sum,
  technologyFeeLine,
  type BoardStatus,
  type ExpensePaymentMethod,
  type PaymentAct,
  type PaymentStageEventType,
  type PaymentStatus,
} from '@evogard/domain';
import type {
  AttachmentUrl,
  InvoiceWindowOut,
  MyPaymentDetail,
  Payment,
  PaymentAction,
  PaymentDetail,
  PaymentFile,
  PaymentTransition,
  PaymentStages,
  PaymentsPage,
} from '@evogard/contract';
import {
  InvoiceForm,
  PaymentFileList,
  PaymentHistory,
  PaymentNotes,
  PaymentStatusBadge,
} from '../../components/PaymentParts.js';
import { StatementClosingModal } from '../../components/StatementClosing.js';
import { BeneficiaryDetailModal } from '../../components/beneficiary/BeneficiaryDetailModal.js';
import { api, errorMessage, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { PAYMENTS_PULSE_KEY } from '../../lib/realtime.js';
import { can, useSessionContext } from '../../lib/session.js';
import { TechnologyFeeModal } from './TechnologyFeeModal.js';

/** Quantos cartões cada página traz. */
const PAGE_SIZE = 50;

/**
 * As fases do quadro como a praça as configurou — o nome, a cor e os eventos
 * de cada uma (02/10/2026). As cinco são sempre as mesmas, na mesma ordem: são
 * a máquina de `@evogard/domain`. O que vem da API é a aparência e o que fica
 * pendurado em cada uma.
 *
 * O consultor lê pela rota dele (`/payment-stages/mine`), só nome e cor. A
 * chave é a mesma para o quadro, a tabela e o cartão aberto, que perguntam o
 * nome da fase cada um no seu lugar.
 */
function usePaymentStages(scope: Scope) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: ['management', 'payment-stages', scope, tenantId],
    queryFn: async (): Promise<PaymentStages> => {
      const failure = 'Não foi possível carregar as fases do quadro.';
      if (scope === 'praca')
        return ok(await api.management.paymentStages(), 200, failure);
      const body = ok(await api.management.myPaymentStages(), 200, failure);
      return {
        stages: body.stages.map((s) => ({ ...s, customized: true, events: [] })),
        accounts: [],
      };
    },
  });
}

/**
 * O nome da fase — o que a praça gravou, ou o de nascença enquanto as fases
 * não chegaram e para "Aguardando NF", que não é coluna de quadro nenhum.
 */
function useStageName(scope: Scope): (status: PaymentStatus) => string {
  const { data } = usePaymentStages(scope);
  return (status) =>
    data?.stages.find((s) => s.status === status)?.name ?? PAYMENT_STATUS_LABELS[status];
}

/**
 * O catálogo e os destinos do editor de eventos. O destino é a conta do Conta
 * Azul, por evento. A conta arquivada depois de escolhida continua na lista —
 * senão o evento gravado apareceria com o número da conta no lugar do nome —,
 * e o servidor recusa usá-la num evento novo.
 */
function eventsConfig(data: PaymentStages): StageEventsConfig {
  const archived = new Map<number, string>();
  for (const s of data.stages) {
    for (const e of s.events) {
      if (e.accountArchived) archived.set(e.accountId, e.accountName);
    }
  }
  return {
    catalog: PAYMENT_STAGE_EVENT_TYPES.map((type) => ({
      type,
      label: PAYMENT_STAGE_EVENT_LABELS[type],
    })),
    targets: [
      ...data.accounts.map((a) => ({ id: String(a.id), label: a.name })),
      ...[...archived].map(([id, name]) => ({
        id: String(id),
        label: `${name} (arquivada)`,
      })),
    ],
    targetLabel: 'Conta do Conta Azul',
    notice: (
      <>
        Quando o cartão <strong>entra</strong> na fase, &ldquo;Lançar conta a pagar&rdquo;
        e &ldquo;Dar baixa&rdquo; executam no Conta Azul, na conta de teste da base, e o
        cartão mostra cada passo. Só a premiação do consultor externo vai, e só o cartão
        que entrar depois de o evento ser salvo. &ldquo;Atualizar situação&rdquo; fica
        gravado e ainda não executa.
      </>
    ),
  };
}

/**
 * `?card=` abre um cartão — é o destino do clique no aviso do sino. Número
 * inválido é ignorado, e não erro: o link velho abre o quadro, que é o que a
 * pessoa ia ver de qualquer jeito.
 */
export const paymentCardSearch = z.object({
  card: z.coerce.number().int().positive().optional().catch(undefined),
});

type View = 'quadro' | 'tabela';

/**
 * De quem é o quadro: da PRAÇA (o Financeiro, `:ver`) ou do PRÓPRIO consultor
 * (`:ver_meus`). As rotas da API são outras em cada um — o consultor nunca lê
 * a praça por um filtro no cliente (regra 10, com outro público).
 */
type Scope = 'praca' | 'meus';

/** O ato que o Financeiro abriu, esperando o formulário. */
interface PendingAct {
  act: Exclude<PaymentAct, 'enviar_nf'>;
  payment: Payment;
  /** Do arrasto: o quadro espera a promessa para manter ou desfazer. */
  settle?: { resolve: () => void; reject: (e: Error) => void };
}

/** O que a pessoa pode fazer no quadro da praça — uma ação do catálogo cada. */
interface Grants {
  open: boolean;
  move: boolean;
  giveBack: boolean;
  receipt: boolean;
  refuse: boolean;
  observe: boolean;
  editWindows: boolean;
  editTechnologyFee: boolean;
}

/** Quem pode o ato: a ação que `PAYMENT_ACT_ACTIONS` diz que ele pede. */
function grantedAct(grants: Grants, act: Exclude<PaymentAct, 'enviar_nf'>): boolean {
  switch (PAYMENT_ACT_ACTIONS[act]) {
    case 'movimentar':
      return grants.move;
    case 'devolver':
      return grants.giveBack;
    case 'anexar_comprovante':
      return grants.receipt;
    case 'recusar':
      return grants.refuse;
    default:
      return false;
  }
}

/**
 * Financeiro › Premiações › Pagamentos (e Meus pagamentos) — o pagamento das
 * premiações, do envio da nota ao comprovante.
 *
 * ## As fases são fixas, e o quadro não oferece criar nenhuma
 *
 * É o segundo uso do `Kanban` de `@evogard/ui`, e passa só o `update` de
 * `stageActions`: as cinco colunas são o caminho da despesa de premiação
 * (`EXPENSE_FLOWS.premiacao` e `packages/domain/src/payment.ts`) — Aprovação
 * Financeiro, Pendente, Aguardando Pagamento, Pagamento Efetuado e Pagamento
 * Recusado. "Pagamento Efetuado" é fato de dinheiro e publica evento; uma
 * coluna criada, arquivada ou reordenada mudaria o que o pagamento quer
 * dizer. **Não há "Aguardando NF"**: o cartão só existe depois que o
 * consultor envia a nota.
 *
 * Desde 02/10/2026 a praça muda o **nome, a cor e os eventos** de cada fase
 * (`management.payment_stages:editar`), pelo "…" da coluna. O nome muda o
 * quadro, a tabela e o cartão aberto; o histórico do cartão e os avisos do
 * sino continuam com o nome de nascença, porque são frase gravada no momento.
 * Desde 27/10/2026 "Lançar conta a pagar" e "Dar baixa" executam no Conta Azul
 * quando o cartão entra na fase, e o cartão aberto mostra os passos
 * (`ContaAzulSection`); "Atualizar situação" continua só gravado. Na visão em
 * tabela, que não tem coluna, o mesmo editor abre pelo botão "Fases" da barra
 * (`KanbanStagesModal`, 08/10/2026).
 *
 * ## Arrastar É o ato, e o ato pede o formulário dele
 *
 * Soltar em "Aguardando Pagamento" abre a aprovação; em "Pendente", o motivo;
 * em "Pagamento Efetuado", a forma e o comprovante; em "Pagamento Recusado", o
 * motivo da recusa. O quadro move na hora e espera: salvar mantém, cancelar
 * desfaz. Arrasto que a máquina não conhece — de volta para "Aprovação
 * Financeiro", por exemplo, que é o reenvio da nota pelo consultor — desfaz
 * com a frase que diz por quê.
 *
 * ## Quem pode o quê — uma ação por ato, desde 24/09/2026
 *
 * `:ver` abre a tela, e alternar entre o quadro e a tabela vem junto;
 * `:abrir` abre o cartão; `:movimentar` arrasta e aprova; `:devolver` volta o
 * cartão ao consultor; `:anexar_comprovante` paga; `:recusar` recusa o
 * pagamento; `:observar` escreve observação; `:ver_comprovante` vê o comprovante (o
 * servidor já o tira da resposta de quem não pode). O que a pessoa não pode
 * não aparece — e quem protege continua sendo a API.
 *
 * ## O consultor vê só a tabela (28/10/2026)
 *
 * `scope="meus"` lê `/payments/mine/board`: os pagamentos dele, sem busca — o
 * primeiro envio é no Painel de Gestão, lote a lote, e esta tela acompanha o
 * que já foi mandado. Até 28/10 ele tinha o quadro, só leitura; saiu por
 * pedido do dono, e com ele a palavra "fase": para o consultor a coluna e o
 * cartão aberto dizem **Status**, e o lote se chama **Recorrência 1x** e
 * **Recorrência 2x**. O Financeiro continua com quadro, fase e lote. O cartão
 * aberto mostra o status, as observações, o histórico e, com `:enviar_nf`, o
 * **reenvio** quando o Financeiro devolve.
 *
 * ## Ao vivo
 *
 * O aviso de pagamento que chega pelo WebSocket (`lib/realtime.tsx`) invalida
 * as consultas de pagamento e muda o pulso (`PAYMENTS_PULSE_KEY`), que está na
 * `reloadKey` do quadro: a devolução aparece na coluna sem recarregar.
 */
export function PaymentsScreen({ scope }: { scope: Scope }) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const grants: Grants = {
    open: can(context, 'management.payments', 'abrir'),
    move: can(context, 'management.payments', 'movimentar'),
    giveBack: can(context, 'management.payments', 'devolver'),
    receipt: can(context, 'management.payments', 'anexar_comprovante'),
    refuse: can(context, 'management.payments', 'recusar'),
    observe: can(context, 'management.payments', 'observar'),
    editWindows: can(context, 'management.invoice_windows', 'editar'),
    editTechnologyFee: can(context, 'management.technology_fee', 'editar'),
  };
  const mine = scope === 'meus';
  const canOpen = mine || grants.open;
  const canEditStages = !mine && can(context, 'management.payment_stages', 'editar');

  const queryClient = useQueryClient();
  const stagesQuery = usePaymentStages(scope);
  const stageName = useStageName(scope);
  const stages: KanbanStage[] = (stagesQuery.data?.stages ?? []).map((s) => ({
    id: s.status,
    name: s.name,
    color: s.color,
    events: s.events.map((e) => ({
      id: String(e.id),
      type: e.type,
      targetId: String(e.accountId),
    })),
  }));
  const stageActions: KanbanStageActions | undefined = canEditStages
    ? {
        update: async (stageId, input) => {
          const saved = ok(
            await api.management.savePaymentStage({
              params: { status: stageId as BoardStatus },
              body: {
                name: input.name,
                color: input.color,
                events: (input.events ?? []).map((e) => ({
                  type: e.type as PaymentStageEventType,
                  accountId: Number(e.targetId),
                })),
              },
            }),
            200,
            'Não foi possível salvar a fase.',
          );
          queryClient.setQueryData(
            ['management', 'payment-stages', scope, tenantId],
            saved,
          );
        },
      }
    : undefined;

  const search = useSearch({ strict: false }) as { card?: number };
  const navigate = useNavigate();
  const openId = search.card ?? null;
  const setOpenId = (id: number | null) =>
    void navigate({
      to: mine ? '/sales/recurrence-payments/mine' : '/sales/recurrence-payments',
      search: id === null ? {} : { card: id },
      replace: true,
    });

  const [view, setView] = useState<View>(mine ? 'tabela' : 'quadro');
  const [searchText, setSearchText] = useState('');
  const term = useDebouncedTerm(searchText.trim());
  const [filters, setFilters] = useState<FilterValues>({});
  /* O filtro é o mês do PAGAMENTO; a API filtra pela competência, o anterior. */
  const month =
    typeof filters.period === 'string' && filters.period ? filters.period : undefined;
  const category =
    filters.lote === '1x' || filters.lote === '2x'
      ? (filters.lote as '1x' | '2x')
      : undefined;

  const [version, setVersion] = useState(0);
  const refresh = () => setVersion((v) => v + 1);
  const known = useRef(new Map<string, Payment>());

  const [pending, setPending] = useState<PendingAct | null>(null);
  const [windowsOpen, setWindowsOpen] = useState(false);
  const [feeOpen, setFeeOpen] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  /*
   * O fechamento da competência mora aqui desde 25/09/2026, ao lado da janela
   * de envio — antes ele era a faixa do topo do Extrato. A permissão é a do
   * comercial, `sales.statement:fechar`, e as rotas são as mesmas.
   */
  const canCloseMonth = can(context, 'sales.statement', 'fechar');

  /* Só leitura: quem escreve é o WebSocket, com `setQueryData`. */
  const { data: pulse = 0 } = useQuery({
    queryKey: PAYMENTS_PULSE_KEY,
    queryFn: () => 0,
    staleTime: Infinity,
  });

  const listQuery = { month, category, search: mine ? undefined : term || undefined };

  const fetchPage = async (
    query: typeof listQuery & {
      status?: PaymentStatus;
      cursor?: string;
      perPage: number;
    },
    failure: string,
  ): Promise<PaymentsPage> => {
    if (!mine) return ok(await api.management.list({ query }), 200, failure);
    const { search: _ignored, ...own } = query;
    return ok(await api.management.myBoard({ query: own }), 200, failure);
  };

  const loadPage = async (stageId: string, cursor: string | null) => {
    const body = await fetchPage(
      {
        ...listQuery,
        status: stageId as PaymentStatus,
        cursor: cursor ?? undefined,
        perPage: PAGE_SIZE,
      },
      'Não foi possível carregar esta fase.',
    );
    for (const p of body.items) known.current.set(String(p.id), p);
    return { items: body.items, next: body.next, total: body.total };
  };

  const moveCard = (move: KanbanMove): Promise<void> | void => {
    /* Dentro da mesma fase não há ordem a gravar: a coluna é ordenada pela
       última mudança, e a próxima leitura a devolve assim. */
    if (move.fromStageId === move.toStageId) return;

    const payment = known.current.get(move.cardId);
    const from = move.fromStageId as PaymentStatus;
    const to = move.toStageId as PaymentStatus;
    const act = payment ? boardAct(from, to, payment.invoiceRequired) : null;
    if (!payment || act === null || act === 'enviar_nf') {
      throw new Error(
        to === 'aprovacao_financeiro'
          ? 'Quem envia a nota é o consultor. O quadro não envia nota de ninguém.'
          : `Daqui o cartão não vai para "${stageName(to)}".`,
      );
    }
    if (!grantedAct(grants, act)) {
      throw new Error(
        act === 'pagar'
          ? 'Mover para "Pagamento Efetuado" é anexar o comprovante, e você não tem essa permissão.'
          : act === 'solicitar_correcao'
            ? 'Voltar o cartão para o consultor pede outra permissão, que você não tem.'
            : act === 'recusar'
              ? 'Recusar o pagamento pede outra permissão, que você não tem.'
              : 'Você não tem permissão para este passo.',
      );
    }
    return new Promise<void>((resolve, reject) => {
      setPending({ act, payment, settle: { resolve, reject } });
    });
  };

  const table = useQuery({
    /* O pulso na chave: sem quadro, é a tabela que mostra a devolução ao vivo
       na visão do consultor. */
    queryKey: [
      'management',
      'payments',
      scope,
      'table',
      tenantId,
      listQuery,
      version,
      pulse,
    ],
    enabled: view === 'tabela',
    queryFn: () =>
      fetchPage(
        { ...listQuery, perPage: 200 },
        loadFailureMessage(undefined, 'os pagamentos'),
      ),
  });

  const filtered = Boolean(term || month || category);

  return (
    <Page measure="cheia">
      <PageHeader
        title={mine ? 'Meus pagamentos' : 'Pagamentos Recorrência'}
        actions={
          <>
            {!mine && (
              <Button
                variant="secundario"
                icon="relogio"
                onClick={() => setWindowsOpen(true)}
              >
                Janela de envio
              </Button>
            )}
            {!mine && (
              <Button
                variant="secundario"
                icon="dinheiro"
                onClick={() => setFeeOpen(true)}
              >
                Taxa de tecnologia
              </Button>
            )}
            {!mine && canCloseMonth && (
              <Button
                variant="secundario"
                icon="bloqueado"
                onClick={() => setClosingOpen(true)}
              >
                Fechar competência
              </Button>
            )}
          </>
        }
      >
        {mine ? (
          <>
            O acompanhamento das notas que você enviou, até o comprovante — com as
            observações do Financeiro. A primeira nota de cada lote é enviada no{' '}
            <Link to="/sales/home" className={linkTextClasses()}>
              Início do Comercial
            </Link>
            .
          </>
        ) : (
          'O pagamento das premiações: a nota fiscal de cada consultor, por lote, até o comprovante.'
        )}
      </PageHeader>

      {/*
       * Sem vínculo de consultor nesta base, "Meus pagamentos" não tem nada da
       * pessoa — o menu já esconde o item (`consultantOnly`), e isto é para
       * quem chega pelo endereço. A frase é a mesma que a API devolve no painel
       * (`sem_vinculo`), e o quadro não é desenhado: cinco colunas vazias
       * diriam "você não tem pagamento", que é outra afirmação.
       */}
      {mine && context && !context.consultant ? (
        <Alert tone="informacao">
          Você não está cadastrado como consultor nesta base, então não há pagamento de
          premiação seu para acompanhar aqui.
        </Alert>
      ) : (
        <>
          {/*
           * "Ver tabela" mora AQUI, na fileira dos filtros, e não nas ações do
           * cabeçalho: os dois mudam como a lista é vista, e no cabeçalho ele ficava
           * uma linha acima e com outra altura (36px contra os 44px da barra). Em
           * "Meus pagamentos", sem busca, os dois controles nem ficavam do mesmo
           * lado da tela.
           */}
          <Toolbar
            search={
              mine
                ? undefined
                : {
                    label: 'Pesquisar consultor',
                    placeholder: 'Nome ou código do ERP',
                    value: searchText,
                    onChange: setSearchText,
                  }
            }
            toolbar={
              <>
                <Filters
                  values={filters}
                  onApply={setFilters}
                  fields={[
                    { type: 'periodo', name: 'period', label: 'Competência' },
                    {
                      type: 'selecao',
                      name: 'lote',
                      label: mine ? 'Recorrência' : 'Lote',
                      placeholder: 'Os dois',
                      options: [
                        { value: '1x', label: categoryLabel('1x', mine) },
                        { value: '2x', label: categoryLabel('2x', mine) },
                      ],
                    },
                  ]}
                />
                {/* No quadro a fase se edita pelo "…" da coluna; a tabela não
                    tem coluna, e o caminho até o mesmo editor é este. */}
                {view === 'tabela' && stageActions?.update && stagesQuery.data && (
                  <Button
                    variant="contorno"
                    icon="etiqueta"
                    onClick={() => setStagesOpen(true)}
                  >
                    Fases
                  </Button>
                )}
                {!mine && (
                  <Button
                    variant="contorno"
                    icon={view === 'quadro' ? 'documento' : 'quadro'}
                    onClick={() => setView(view === 'quadro' ? 'tabela' : 'quadro')}
                  >
                    {view === 'quadro' ? 'Ver tabela' : 'Ver quadro'}
                  </Button>
                )}
              </>
            }
          />

          {view === 'quadro' && !stagesQuery.data ? (
            stagesQuery.isError ? (
              <Alert tone="erro">{stagesQuery.error.message}</Alert>
            ) : (
              <p className="text-sm text-texto-suave">Carregando o quadro…</p>
            )
          ) : view === 'quadro' ? (
            <Kanban<Payment>
              label="Quadro de pagamentos das premiações"
              stages={stages}
              stageActions={stageActions}
              stageEvents={stagesQuery.data ? eventsConfig(stagesQuery.data) : undefined}
              cardId={(p) => String(p.id)}
              cardLabel={(p) =>
                `${p.consultant.nome}, lote ${p.category} da premiação ${monthLabel(p.period)}`
              }
              loadPage={loadPage}
              reloadKey={JSON.stringify([tenantId, scope, version, pulse, listQuery])}
              renderCard={(p) => <PaymentCardBody payment={p} />}
              onMoveCard={grants.move ? moveCard : undefined}
              onOpenCard={canOpen ? (p) => setOpenId(p.id) : undefined}
              emptyColumnText={
                filtered
                  ? 'Nenhum pagamento desta fase com esses filtros.'
                  : 'Nenhum pagamento nesta fase.'
              }
            />
          ) : (
            <Card bleed>
              {table.isError && <Alert tone="erro">{table.error.message}</Alert>}
              <Table
                label={mine ? 'Meus pagamentos' : 'Pagamentos das premiações'}
                items={table.data?.items ?? []}
                rowKey={(p) => String(p.id)}
                rowActions={
                  canOpen
                    ? (p) => [
                        { label: 'Abrir', icon: 'olho', run: () => setOpenId(p.id) },
                      ]
                    : undefined
                }
                empty={
                  table.isPending
                    ? 'Carregando…'
                    : mine
                      ? 'Nenhuma nota enviada. A primeira nota de cada lote é enviada no Início do Comercial.'
                      : 'Nenhuma nota recebida. O cartão aparece aqui quando o consultor envia a nota do lote.'
                }
                columns={[
                  ...(mine
                    ? []
                    : [
                        {
                          title: 'Consultor',
                          minWidth: '14rem',
                          cell: (p: Payment) => p.consultant.nome,
                        },
                      ]),
                  {
                    title: 'Competência',
                    minWidth: '7rem',
                    cell: (p) => monthLabel(p.period),
                  },
                  {
                    title: mine ? 'Premiação' : 'Lote',
                    minWidth: mine ? '9rem' : '4rem',
                    cell: (p) => categoryLabel(p.category, mine),
                  },
                  {
                    title: 'Valor',
                    minWidth: '8rem',
                    cell: (p) => format(fromColumn(p.amount)!),
                  },
                  {
                    title: mine ? 'Status' : 'Fase',
                    minWidth: '10rem',
                    cell: (p) => (
                      <PaymentStatusBadge status={p.status} label={stageName(p.status)} />
                    ),
                  },
                  {
                    title: 'Prazo da nota',
                    minWidth: '8rem',
                    cell: (p) => (p.sentLate ? 'Atrasada' : 'No prazo'),
                  },
                  {
                    title: 'Pagamento previsto',
                    minWidth: '9rem',
                    cell: (p) => (p.dueDate ? formatDate(p.dueDate) : '—'),
                  },
                ]}
              />
              {table.data && table.data.total > table.data.items.length && (
                <p className="px-4 pb-4 text-sm text-texto-suave">
                  Mostrando {table.data.items.length} de {table.data.total}. Use os
                  filtros para chegar aos outros.
                </p>
              )}
            </Card>
          )}
        </>
      )}

      {openId !== null &&
        (mine ? (
          <MyPaymentModal
            id={openId}
            canSend={can(context, 'management.payments', 'enviar_nf')}
            onClose={() => setOpenId(null)}
            onChanged={refresh}
          />
        ) : (
          <PaymentDetailModal
            id={openId}
            grants={grants}
            onClose={() => setOpenId(null)}
            onAct={(act, payment) => setPending({ act, payment })}
            onChanged={refresh}
          />
        ))}

      {pending && (
        <ActModal
          pending={pending}
          onDone={() => {
            pending.settle?.resolve();
            setPending(null);
            refresh();
          }}
          onCancel={() => {
            pending.settle?.reject(
              new Error('Nada mudou: o cartão voltou para onde estava.'),
            );
            setPending(null);
          }}
        />
      )}

      {stageActions?.update && stagesQuery.data && (
        <KanbanStagesModal
          open={stagesOpen}
          onOpenChange={setStagesOpen}
          stages={stages}
          update={stageActions.update}
          stageEvents={eventsConfig(stagesQuery.data)}
          emptyColumnText="Nenhum pagamento nesta fase."
        />
      )}
      {closingOpen && <StatementClosingModal onClose={() => setClosingOpen(false)} />}
      {windowsOpen && (
        <WindowsModal
          canEdit={grants.editWindows}
          onClose={() => setWindowsOpen(false)}
        />
      )}
      {feeOpen && (
        <TechnologyFeeModal
          canEdit={grants.editTechnologyFee}
          onClose={() => setFeeOpen(false)}
        />
      )}
    </Page>
  );
}

/**
 * O lote como cada um o chama: "1x" para o Financeiro, "Recorrência 1x" para o
 * consultor — o nome que ele vê nas premiações dele.
 */
function categoryLabel(category: string, mine: boolean): string {
  return mine ? `Recorrência ${category}` : category;
}

/** O miolo do cartão no quadro. Sem botão dentro — ver o `Kanban`. */
function PaymentCardBody({ payment: p }: { payment: Payment }) {
  return (
    <KanbanCardBody
      title={p.consultant.nome}
      tags={
        <>
          <KanbanTag>Lote {p.category}</KanbanTag>
          <KanbanTag>Premiação {monthLabel(p.period)}</KanbanTag>
          {!p.invoiceRequired && <KanbanTag>Sem nota · interno</KanbanTag>}
          {p.sentLate && <KanbanTag tone="erro">{LATE_SEND_LABEL}</KanbanTag>}
          {!p.closed && <KanbanTag tone="erro">Competência reaberta</KanbanTag>}
        </>
      }
      detail={
        p.status === 'pagamento_efetuado' && p.paidAmount !== null
          ? `${format(fromColumn(p.paidAmount)!)} pagos em ${formatDate(p.paidAt!)}`
          : `Valor: ${format(fromColumn(p.amount)!)}` +
            (p.dueDate && p.status !== 'pagamento_recusado'
              ? ` · pagar em ${formatDate(p.dueDate)}`
              : '')
      }
      detailIcon="dinheiro"
    />
  );
}

/* ------------------------------------------------------------------------- *
 * O cartão aberto — o Financeiro
 * ------------------------------------------------------------------------- */

function PaymentDetailModal({
  id,
  grants,
  onClose,
  onAct,
  onChanged,
}: {
  id: number;
  grants: Grants;
  onClose: () => void;
  onAct: (act: PendingAct['act'], payment: Payment) => void;
  onChanged: () => void;
}) {
  const query = useQuery({
    queryKey: ['management', 'payments', 'detail', id],
    queryFn: async () =>
      ok(
        await api.management.detail({ params: { id } }),
        200,
        'Não foi possível abrir o cartão.',
      ),
  });
  const d = query.data;

  const actions: { act: PendingAct['act']; label: string; variant?: 'contorno' }[] = [];
  if (d) {
    if (grants.giveBack && paymentTransition(d.status, 'solicitar_correcao') !== null) {
      actions.push({
        act: 'solicitar_correcao',
        label: actTitle('solicitar_correcao', d.invoiceRequired),
        variant: 'contorno',
      });
    }
    if (
      grants.move &&
      actFitsLot('retomar', d.invoiceRequired) &&
      paymentTransition(d.status, 'retomar') !== null
    ) {
      actions.push({ act: 'retomar', label: actTitle('retomar', d.invoiceRequired) });
    }
    if (grants.refuse && paymentTransition(d.status, 'recusar') !== null) {
      actions.push({ act: 'recusar', label: 'Recusar o pagamento', variant: 'contorno' });
    }
    if (grants.move && paymentTransition(d.status, 'aprovar') !== null) {
      actions.push({ act: 'aprovar', label: actTitle('aprovar', d.invoiceRequired) });
    }
    if (grants.receipt && paymentTransition(d.status, 'pagar') !== null) {
      actions.push({ act: 'pagar', label: 'Anexar o comprovante' });
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={d ? `${d.consultant.nome} — lote ${d.category}` : 'Pagamento'}
      descricao={d ? `Competência ${monthLabel(d.period)}` : undefined}
      size="lg"
    >
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {d && (
        <div className="flex flex-col gap-5">
          <DetailSummary payment={d} />

          <PayeeSection beneficiaryId={d.beneficiaryId} />

          <FilesSection
            detail={d}
            empty="Nenhuma nota enviada ainda."
            sign={async (fileId) =>
              ok(
                await api.management.fileUrl({ params: { id: d.id, fileId } }),
                200,
                'Não foi possível abrir o arquivo.',
              )
            }
          />

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">Observações</h3>
            <PaymentNotes notes={d.notes} />
            {grants.observe && <NoteForm id={d.id} onAdded={onChanged} />}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-bold">Histórico</h3>
            <PaymentHistory transitions={d.transitions} />
          </section>

          {d.contaAzul && <ContaAzulSection contaAzul={d.contaAzul} />}

          {actions.length > 0 && (
            <ModalActions>
              {actions.map((a) => (
                <Button key={a.act} variant={a.variant} onClick={() => onAct(a.act, d)}>
                  {a.label}
                </Button>
              ))}
            </ModalActions>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * O lote no Conta Azul (27/10/2026): os passos que os eventos de fase deram
 * lá, um por linha. Sem passo nenhum a seção não aparece — o cartão que nunca
 * entrou numa fase com evento não tem o que dizer sobre o Conta Azul.
 */
function ContaAzulSection({
  contaAzul,
}: {
  contaAzul: NonNullable<PaymentDetail['contaAzul']>;
}) {
  if (contaAzul.steps.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-bold">Conta Azul</h3>
      <ol className="flex flex-col gap-2 text-sm">
        {contaAzul.steps.map((step, i) => (
          <li key={i} className="flex flex-col">
            <span>
              <strong>{CONTAAZUL_STEP_LABELS[step.kind]}</strong>
              <span className="text-texto-suave"> · {formatInstant(step.at)}</span>
            </span>
            <span className="text-texto-suave">{step.message}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

// @valor — as chaves são o enum do contrato.
const CONTAAZUL_STEP_LABELS: Record<
  NonNullable<PaymentDetail['contaAzul']>['steps'][number]['kind'],
  string
> = {
  enviando: 'Enviando',
  lancada: 'Lançada',
  baixada: 'Baixada',
  fornecedor_criado: 'Fornecedor criado',
  recusada: 'Não foi para o Conta Azul',
  sem_resposta: 'Sem resposta',
};

/**
 * Para onde pagar — o beneficiário da despesa, que é o consultor do lote.
 *
 * O cartão não traz os dados bancários: eles saem do cadastro de beneficiário
 * (`BeneficiaryDetailModal`), sob `platform.beneficiaries:ver_dados_bancarios`,
 * e cada abertura fica na trilha de auditoria. Abrir é um clique, e não parte
 * de abrir o cartão, pelo mesmo motivo: quem só confere a nota não precisa
 * ver a conta de ninguém.
 */
function PayeeSection({ beneficiaryId }: { beneficiaryId: number | null }) {
  const { data: context } = useSessionContext();
  const [open, setOpen] = useState(false);
  const canSee = can(context, 'platform.beneficiaries', 'ver_dados_bancarios');

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-bold">Para onde pagar</h3>
      {beneficiaryId === null ? (
        <NotProvided>O beneficiário nasce com o envio da nota.</NotProvided>
      ) : canSee ? (
        <div>
          <Button variant="secundario" icon="dinheiro" onClick={() => setOpen(true)}>
            Ver dados bancários do consultor
          </Button>
        </div>
      ) : (
        <NotProvided>
          Os dados bancários do consultor estão no cadastro de beneficiários, e ver a
          conta pede outra permissão. Fale com quem administra os grupos.
        </NotProvided>
      )}
      {open && (
        <BeneficiaryDetailModal
          beneficiaryId={beneficiaryId}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

/** A observação nova. O consultor lê e é avisado — a tela diz isso ao lado. */
function NoteForm({ id, onAdded }: { id: number; onAdded: () => void }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.management.addNote({
        params: { id },
        body: { body: text.trim() },
      });
      if (r.status !== 201) throw new Error(errorMessage(r.body));
      queryClient.setQueryData<PaymentDetail>(
        ['management', 'payments', 'detail', id],
        r.body,
      );
      setText('');
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) void save();
      }}
    >
      <TextArea
        label="Nova observação"
        maxLength={2000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        help="O consultor lê esta observação no quadro dele e recebe um aviso."
      />
      {error && <Alert tone="erro">{error}</Alert>}
      <div>
        <Button type="submit" variant="secundario" loading={busy} disabled={!text.trim()}>
          Adicionar observação
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * O cartão aberto — o PRÓPRIO consultor
 * ------------------------------------------------------------------------- */

/**
 * O cartão do PRÓPRIO consultor — um recibo, e não a ficha de trabalho do
 * Financeiro (28/10/2026, pedido do dono).
 *
 * À esquerda (55%): o aviso do que está acontecendo, o recibo (valor bruto, a
 * taxa de tecnologia e o líquido em destaque) e os anexos, separados por quem
 * os pôs ali. À direita (45%): a linha do tempo. No telefone, uma coluna.
 *
 * O que ficou de fora de propósito: a janela de envio e a frase de atraso — o
 * consultor sabe quando enviou —, e a explicação da entrada em "Aprovação
 * Financeiro", que repetia o recibo. As cores são os tokens da casa, e não os
 * hexadecimais da referência: o modal precisa valer no tema escuro também.
 */
export function MyPaymentModal({
  id,
  canSend,
  onClose,
  onChanged,
}: {
  id: number;
  canSend: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const key = ['management', 'payments', 'mine', 'detail', id];
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      ok(
        await api.management.myBoardDetail({ params: { id } }),
        200,
        'Não foi possível abrir o cartão.',
      ),
  });
  const d: MyPaymentDetail | undefined = query.data;
  const stageName = useStageName('meus');
  /* O quadro só REENVIA: o primeiro envio é no Início do Comercial. */
  const resendable = d?.status === 'pendente' && d.sendRefusal === null;
  const sign = async (fileId: number) =>
    ok(
      await api.management.myBoardFileUrl({ params: { id, fileId } }),
      200,
      'Não foi possível abrir o arquivo.',
    );

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        d
          ? `${categoryLabel(d.category, true)} — premiação ${monthLabel(d.period)}`
          : 'Pagamento'
      }
      titleAddon={
        d ? (
          <PaymentStatusBadge status={d.status} label={stageName(d.status)} />
        ) : undefined
      }
      size="lg"
    >
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {d && (
        <div className="grid items-start gap-6 md:grid-cols-[11fr_9fr]">
          <div className="flex min-w-0 flex-col gap-4">
            <MyStatusNotice payment={d} />
            <MyReceipt payment={d} />

            {d.status === 'pendente' && (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-bold">Reenviar a nota</h3>
                {!canSend ? (
                  <NotProvided>
                    Você acompanha este cartão, mas o reenvio da nota pede outra
                    permissão. Fale com quem administra os grupos.
                  </NotProvided>
                ) : resendable ? (
                  <InvoiceForm
                    payment={d}
                    onSent={() => {
                      void queryClient.invalidateQueries({ queryKey: key });
                      void queryClient.invalidateQueries({
                        queryKey: ['management', 'payments', 'mine'],
                      });
                      onChanged();
                    }}
                  />
                ) : (
                  d.sendRefusal !== null && <NotProvided>{d.sendRefusal}</NotProvided>
                )}
              </section>
            )}

            <MyAttachments files={d.files} sign={sign} />

            {/* Sem observação, a seção some — "nenhuma ainda" não informa nada. */}
            {d.notes.length > 0 && (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-bold">Observações do Financeiro</h3>
                <PaymentNotes notes={d.notes} />
              </section>
            )}
          </div>

          <MyTimeline transitions={d.transitions} />
        </div>
      )}
    </Modal>
  );
}

/**
 * O aviso do alto: o que está acontecendo com este pagamento, numa frase.
 *
 * Os avisos que já existiam continuam (competência reaberta, correção pedida,
 * pagamento recusado); os outros são a frase amigável de cada situação.
 */
function MyStatusNotice({ payment: d }: { payment: MyPaymentDetail }) {
  if (!d.closed) {
    return (
      <Alert tone="alerta" title="A competência foi reaberta no comercial.">
        O valor apurado pode mudar. A nota deste lote não sai até ela fechar de novo.
      </Alert>
    );
  }
  switch (d.status) {
    /* Pago não tem aviso: o selo ao lado do título já diz (28/10/2026). */
    case 'pagamento_recusado':
      return (
        <Alert tone="erro" title="Pagamento recusado">
          {d.refusalReason ?? 'O motivo não foi registrado.'}
        </Alert>
      );
    case 'pendente':
      return (
        <Alert tone="alerta" title="O Financeiro pediu correção">
          {d.correctionReason ?? 'Reenvie a nota abaixo.'}
        </Alert>
      );
    case 'aguardando_pagamento':
      return (
        <Alert tone="informacao" title="Nota aprovada">
          {d.dueDate
            ? `Pagamento previsto para ${formatDate(d.dueDate)}.`
            : 'O Financeiro agenda o pagamento.'}
        </Alert>
      );
    case 'aprovacao_financeiro':
      return (
        <Alert tone="informacao" title="Nota em aprovação">
          {d.invoiceSentAt
            ? `Enviada em ${formatInstant(d.invoiceSentAt)}. O Financeiro confere e aprova.`
            : 'O Financeiro confere e aprova.'}
        </Alert>
      );
    case 'aguardando_nf':
      return (
        <Alert tone="alerta" title="Aguardando a sua nota fiscal">
          A nota deste lote é enviada no{' '}
          <Link to="/sales/home" className={linkTextClasses()}>
            Início do Comercial
          </Link>
          , no cartão da {categoryLabel(d.category, true)}.
        </Alert>
      );
    default:
      return null;
  }
}

/**
 * O RECIBO: o valor bruto, a taxa de tecnologia descontada e o líquido em
 * destaque. "Bruto" é o apurado — o líquido somado à taxa —, e o líquido é o
 * PAGO quando o pagamento saiu, ou o que será pago.
 */
function MyReceipt({ payment: d }: { payment: MyPaymentDetail }) {
  const net = fromColumn(d.amount)!;
  const fee =
    d.technologyFee.state === 'aplicada' && d.technologyFee.amount !== null
      ? fromColumn(d.technologyFee.amount)!
      : null;
  const paid = d.status === 'pagamento_efetuado' && d.paidAmount !== null;
  const shown = paid ? fromColumn(d.paidAmount)! : net;

  return (
    <section className="flex flex-col overflow-hidden rounded-cartao border border-borda">
      <dl className="flex flex-col gap-2 px-4 py-3 text-sm">
        {fee !== null && fee !== 0 && (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-texto-suave">Valor bruto</dt>
              <dd className="tabular-nums">{format(sum(net, fee))}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-texto-suave">Taxa de tecnologia</dt>
              <dd className="tabular-nums text-erro">− {format(fee)}</dd>
            </div>
          </>
        )}
        {d.technologyFee.state === 'faixas_nao_definidas' && (
          <p className="text-xs text-texto-suave">
            {technologyFeeLine(d.technologyFee, d.amount)}
          </p>
        )}
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-texto-suave">Nota enviada</dt>
          <dd>{d.invoiceSentAt ? formatInstant(d.invoiceSentAt) : '—'}</dd>
        </div>
      </dl>
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 px-4 py-3',
          paid ? 'bg-sucesso-suave text-sucesso' : 'bg-superficie-suave text-texto',
        )}
      >
        <span className="text-sm font-semibold">
          {paid ? 'Valor líquido pago' : 'Valor líquido a receber'}
        </span>
        <span className="text-xl font-extrabold tabular-nums">{format(shown)}</span>
      </div>
    </section>
  );
}

/**
 * Os anexos, por quem os pôs ali — a nota fiscal do consultor e o comprovante
 * do Financeiro —, cada um numa linha com o nome, o tipo, o tamanho e o botão
 * de baixar.
 */
function MyAttachments({
  files,
  sign,
}: {
  files: readonly PaymentFile[];
  sign: (fileId: number) => Promise<AttachmentUrl>;
}) {
  const [error, setError] = useState<string | null>(null);
  const open = async (fileId: number) => {
    setError(null);
    try {
      const { url } = await sign(fileId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível abrir o arquivo.');
    }
  };

  const groups = [
    {
      title: 'Sua nota fiscal',
      files: files.filter((f) => f.kind === 'nota_fiscal'),
      empty: 'Você ainda não enviou a nota deste lote.',
    },
    {
      title: 'Comprovante do Financeiro',
      files: files.filter((f) => f.kind === 'comprovante'),
      empty: 'O Financeiro anexa o comprovante ao pagar.',
    },
  ];

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-bold">Anexos</h3>
      <div className="flex flex-col overflow-hidden rounded-cartao border border-borda">
        {groups.map((g) => (
          <div key={g.title} className="border-b border-borda-suave last:border-b-0">
            <p className="bg-superficie-suave px-4 py-1.5 text-xs font-semibold text-texto-suave">
              {g.title}
            </p>
            {g.files.length === 0 ? (
              <p className="px-4 py-2.5 text-sm text-texto-suave">{g.empty}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-borda-suave">
                {g.files.map((file) => (
                  <li key={file.id} className="flex items-center gap-3 px-4 py-2.5">
                    <Icon
                      nome="documento"
                      size={20}
                      className="shrink-0 text-texto-suave"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">{file.nome}</span>
                      <span className="text-xs text-texto-suave">
                        {extensionOf(file.nome)} · {formatBytes(file.sizeBytes)} ·{' '}
                        {formatInstant(file.createdAt)}
                        {file.createdBy && ` · ${file.createdBy}`}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="discreto"
                      onClick={() => void open(file.id)}
                      aria-label={`Baixar ${file.nome}`}
                    >
                      <Icon nome="baixar" size={16} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-erro">{error}</p>}
    </section>
  );
}

/** "nota.pdf" → "PDF"; sem extensão, "Arquivo". */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : 'Arquivo';
}

/**
 * A LINHA DO TEMPO: cada etapa com o marcador na cor da situação, quem fez e
 * quando. A entrada em "Aprovação Financeiro" vai sem a frase — ela repetia o
 * recibo ao lado.
 */
function MyTimeline({ transitions }: { transitions: readonly PaymentTransition[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-bold">Histórico</h3>
      <ol className="relative flex flex-col gap-4 border-l border-borda pl-5">
        {transitions.map((t, i) => (
          <li key={i} className="relative flex flex-col gap-0.5 text-sm">
            <span
              aria-hidden="true"
              className={cn(
                'absolute -left-[1.6rem] top-1 size-3 rounded-full ring-4 ring-superficie',
                TIMELINE_DOT[t.to],
              )}
            />
            <strong>{PAYMENT_STATUS_LABELS[t.to]}</strong>
            <span className="text-xs text-texto-suave">
              {formatInstant(t.at)} · {t.by ?? 'Sistema'}
            </span>
            {t.note && t.to !== 'aprovacao_financeiro' && (
              <span className="text-xs text-texto-suave">{t.note}</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** A cor do marcador de cada etapa — verde no pago, vermelho no recusado. */
// @valor
const TIMELINE_DOT: Record<PaymentTransition['to'], string> = {
  aguardando_nf: 'bg-fase-cinza',
  aguardando_lancamento: 'bg-fase-cinza',
  aprovacao_financeiro: 'bg-fase-azul',
  pendente: 'bg-alerta',
  aguardando_pagamento: 'bg-fase-azul',
  pagamento_efetuado: 'bg-sucesso',
  pagamento_recusado: 'bg-erro',
};

/* ------------------------------------------------------------------------- *
 * O que os dois cartões desenham
 * ------------------------------------------------------------------------- */

function FilesSection({
  detail: d,
  empty,
  sign,
}: {
  detail: PaymentDetail;
  empty: string;
  sign: (fileId: number) => Promise<AttachmentUrl>;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-bold">Arquivos</h3>
      {d.files.length === 0 ? (
        <NotProvided>{empty}</NotProvided>
      ) : (
        <PaymentFileList files={d.files} sign={sign} />
      )}
    </section>
  );
}

function DetailSummary({
  payment: d,
  mine = false,
}: {
  payment: Payment | MyPaymentDetail;
  mine?: boolean;
}) {
  const settled = d.status === 'pagamento_efetuado' || d.status === 'pagamento_recusado';
  const stageName = useStageName(mine ? 'meus' : 'praca');
  return (
    <>
      {!d.closed && (
        <Alert tone="alerta" title="A competência foi reaberta no comercial.">
          O valor apurado pode mudar. A nota deste lote não sai até ela fechar de novo.
        </Alert>
      )}
      {d.status === 'pendente' && d.correctionReason && (
        <Alert
          tone="alerta"
          title={mine ? 'O Financeiro pediu correção' : 'Devolvida ao consultor'}
        >
          {d.correctionReason}
        </Alert>
      )}
      {d.status === 'pagamento_recusado' && (
        <Alert tone="erro" title="Pagamento recusado">
          {d.refusalReason ?? 'O motivo não foi registrado.'}
        </Alert>
      )}
      {d.sentLate && (
        <Alert tone="alerta" title={LATE_SEND_LABEL}>
          A primeira nota deste lote chegou depois do fim da janela de envio.
        </Alert>
      )}
      <DataList>
        <DataRow label={mine ? 'Status' : 'Fase'}>
          <PaymentStatusBadge status={d.status} label={stageName(d.status)} />
        </DataRow>
        <DataRow label="Valor">{format(fromColumn(d.amount)!)}</DataRow>
        {/*
         * O consultor vê só a taxa (28/10/2026, pedido do dono); a conta por
         * extenso é do Financeiro. A frase da praça sem faixas fica nos dois:
         * é ela que diz por que a nota não pode ser enviada.
         */}
        {mine &&
        d.technologyFee.state === 'aplicada' &&
        d.technologyFee.amount !== null ? (
          <DataRow label="Taxa de tecnologia">
            − {format(fromColumn(d.technologyFee.amount)!)}
          </DataRow>
        ) : (
          technologyFeeLine(d.technologyFee, d.amount) !== null && (
            <DataRow label="Taxa de tecnologia">
              {technologyFeeLine(d.technologyFee, d.amount)}
            </DataRow>
          )
        )}
        {!settled && (
          <DataRow label="Pagamento previsto">
            {d.dueDate ? formatDate(d.dueDate) : 'a data nasce com o envio da nota'}
          </DataRow>
        )}
        {/* O consultor sabe quando atrasou; a janela por extenso é do Financeiro. */}
        {'window' in d && !mine && (
          <DataRow label="Janela de envio">{windowLabel(d)}</DataRow>
        )}
        <DataRow label="Nota enviada">
          {d.invoiceSentAt ? formatInstant(d.invoiceSentAt) : '—'}
        </DataRow>
        {d.paymentMethod && d.status !== 'pagamento_efetuado' && (
          <DataRow label="Forma de pagamento">
            {EXPENSE_PAYMENT_METHOD_LABELS[d.paymentMethod]}
            <span className="text-texto-suave"> · o Financeiro confirma ao pagar</span>
          </DataRow>
        )}
        {d.status === 'pagamento_efetuado' && d.paidAmount !== null && (
          <DataRow label="Pago">
            {/* para o consultor, só a data e a forma — o valor está logo acima */}
            {mine
              ? formatDate(d.paidAt!)
              : `${format(fromColumn(d.paidAmount)!)} em ${formatDate(d.paidAt!)}`}
            {d.paymentMethod && ` · ${EXPENSE_PAYMENT_METHOD_LABELS[d.paymentMethod]}`}
          </DataRow>
        )}
      </DataList>
    </>
  );
}

/* ------------------------------------------------------------------------- *
 * Os atos
 * ------------------------------------------------------------------------- */

// @valor
const ACT_TITLES: Record<PendingAct['act'], string> = {
  aprovar: 'Aprovar a nota',
  solicitar_correcao: 'Voltar para o consultor',
  pagar: 'Anexar o comprovante',
  recusar: 'Recusar o pagamento',
  retomar: 'Retomar a aprovação',
};

// @valor
const ACT_SUBMIT: Record<PendingAct['act'], string> = {
  aprovar: 'Aprovar',
  solicitar_correcao: 'Voltar para o consultor',
  pagar: 'Marcar como pago',
  recusar: 'Recusar o pagamento',
  retomar: 'Retomar',
};

/**
 * O lote SEM nota (consultor interno) não tem nota a aprovar nem consultor a
 * quem devolver: a pendência é do Financeiro, que corrige e retoma.
 */
// @valor
const ACT_TITLES_NO_INVOICE: Partial<Record<PendingAct['act'], string>> = {
  aprovar: 'Aprovar o pagamento',
  solicitar_correcao: 'Pôr em pendência',
};

function actTitle(act: PendingAct['act'], invoiceRequired: boolean): string {
  return (!invoiceRequired && ACT_TITLES_NO_INVOICE[act]) || ACT_TITLES[act];
}

function actSubmit(act: PendingAct['act'], invoiceRequired: boolean): string {
  return (!invoiceRequired && ACT_TITLES_NO_INVOICE[act]) || ACT_SUBMIT[act];
}

function ActModal({
  pending,
  onDone,
  onCancel,
}: {
  pending: PendingAct;
  onDone: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { act, payment: p } = pending;
  const [note, setNote] = useState('');
  const [paidAt, setPaidAt] = useState(businessDate(new Date()));
  const [method, setMethod] = useState<ExpensePaymentMethod>(
    p.paymentMethod ?? AWARD_DEFAULT_PAYMENT_METHOD,
  );
  const receipt = useAttachments();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let body: PaymentAction;
      if (act === 'aprovar') {
        body = { action: 'aprovar', note: note.trim() || undefined };
      } else if (act === 'solicitar_correcao') {
        body = { action: 'solicitar_correcao', reason: note.trim() };
      } else if (act === 'recusar') {
        body = { action: 'recusar', reason: note.trim() };
      } else if (act === 'retomar') {
        body = { action: 'retomar', note: note.trim() || undefined };
      } else {
        const file = receipt.items[0]!;
        body = {
          action: 'pagar',
          paidAt,
          paymentMethod: method,
          receipt: {
            nome: file.nome,
            typeMime: file.typeMime,
            content: await readAttachmentContent(file.file),
          },
        };
      }
      const r = await api.management.act({ params: { id: p.id }, body });
      if (r.status !== 200) throw new Error(errorMessage(r.body));
      queryClient.setQueryData<PaymentDetail>(
        ['management', 'payments', 'detail', p.id],
        r.body,
      );
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  const ready =
    act === 'aprovar' || act === 'retomar'
      ? true
      : act === 'solicitar_correcao' || act === 'recusar'
        ? note.trim().length >= 3
        : receipt.items.length === 1 && Boolean(paidAt);

  return (
    <Modal
      open
      onOpenChange={(open) => !open && !busy && onCancel()}
      title={actTitle(act, p.invoiceRequired)}
      descricao={`${p.consultant.nome} · lote ${p.category} da premiação ${monthLabel(p.period)} · ${format(fromColumn(p.amount)!)}`}
      size="md"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) void submit();
        }}
      >
        {act === 'aprovar' && (
          <TextArea
            label="Observação"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            help="Opcional. O consultor lê esta observação junto com o aviso de aprovação."
          />
        )}

        {act === 'solicitar_correcao' &&
          (p.invoiceRequired ? (
            <TextArea
              label="O que o consultor precisa corrigir"
              required
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              help="Ele recebe um aviso com esta frase e pode reenviar a nota pelo quadro dele."
            />
          ) : (
            <TextArea
              label="O que está errado nos dados"
              required
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              help="Consultor interno não envia nota: quem corrige é o Financeiro, e depois retoma o cartão para a aprovação. O consultor recebe um aviso com esta frase."
            />
          ))}

        {act === 'retomar' && (
          <TextArea
            label="Observação"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            help="Opcional. O que foi corrigido — o consultor lê junto com o aviso."
          />
        )}

        {act === 'recusar' && (
          <>
            <Alert tone="alerta" title="A recusa é final.">
              O lote não recebe outra nota, e a premiação dele não é paga.
            </Alert>
            <TextArea
              label="Por que o pagamento é recusado"
              required
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              help="O consultor recebe um aviso com esta frase."
            />
          </>
        )}

        {act === 'pagar' && (
          <>
            <p className="text-sm">
              O valor pago é o da despesa:{' '}
              <strong>{format(fromColumn(p.amount)!)}</strong>.
            </p>
            <Select
              label="Forma de pagamento"
              required
              value={method}
              onChange={(e) => setMethod(e.target.value as ExpensePaymentMethod)}
            >
              {EXPENSE_PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {EXPENSE_PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
            <DateField
              label="Data do pagamento"
              required
              value={paidAt}
              onChange={setPaidAt}
              help="A do comprovante. Não pode estar no futuro."
            />
            <AttachmentField
              label="Comprovante"
              items={receipt.items}
              onAdd={receipt.add}
              onRemove={receipt.remove}
              max={1}
              disabled={busy}
            />
          </>
        )}

        {error && <Alert tone="erro">{error}</Alert>}

        <ModalActions>
          <Button variant="contorno" disabled={busy} onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy} disabled={!ready}>
            {actSubmit(act, p.invoiceRequired)}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------------- *
 * A janela de envio
 * ------------------------------------------------------------------------- */

function WindowsModal({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['management', 'invoice-windows'],
    queryFn: async () =>
      ok(await api.management.windows(), 200, 'Não foi possível ler a janela de envio.'),
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Janela de envio da nota"
      descricao="Os dias do mês da própria competência em que o consultor envia a nota do lote — a competência de setembro tem a janela em setembro. Depois do prazo ele ainda envia, e o cartão chega marcado como atrasado. O dia do pagamento vale no mesmo mês para a nota no prazo, e no mês seguinte para a atrasada — é o vencimento da despesa. Lote sem janela ou sem dia de pagamento não recebe nota — e o consultor lê isso no painel."
      size="md"
    >
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.data && (
        <WindowsForm
          initial={query.data}
          canEdit={canEdit}
          onSaved={(saved) => {
            queryClient.setQueryData(['management', 'invoice-windows'], saved);
            onClose();
          }}
          onCancel={onClose}
        />
      )}
    </Modal>
  );
}

function WindowsForm({
  initial,
  canEdit,
  onSaved,
  onCancel,
}: {
  initial: InvoiceWindowOut[];
  canEdit: boolean;
  onSaved: (saved: InvoiceWindowOut[]) => void;
  onCancel: () => void;
}) {
  const start = (category: '1x' | '2x') => {
    const w = initial.find((x) => x.category === category);
    return {
      opens: w ? String(w.opensDay) : '',
      closes: w ? String(w.closesDay) : '',
      pay: w?.payDay ? String(w.payDay) : '',
    };
  };
  const [values, setValues] = useState({ '1x': start('1x'), '2x': start('2x') });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const windows = (['1x', '2x'] as const)
        .filter((c) => values[c].opens.trim() || values[c].closes.trim())
        .map((c) => ({
          category: c,
          opensDay: Number(values[c].opens),
          closesDay: Number(values[c].closes),
          payDay: values[c].pay.trim() ? Number(values[c].pay) : null,
        }));
      const r = await api.management.saveWindows({ body: { windows } });
      if (r.status !== 200) throw new Error(errorMessage(r.body));
      onSaved(r.body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canEdit) void save();
      }}
    >
      {initial.length === 0 && (
        <Alert tone="alerta" title="Nenhuma janela definida nesta base.">
          Enquanto não houver janela e dia de pagamento, nenhum consultor consegue enviar
          a nota.
        </Alert>
      )}
      {(['1x', '2x'] as const).map((c) => (
        <fieldset key={c} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <legend className="mb-2 text-sm font-bold">Lote {c}</legend>
          <Field
            label="Abre no dia"
            inputMode="numeric"
            disabled={!canEdit}
            value={values[c].opens}
            onChange={(e) =>
              setValues((v) => ({ ...v, [c]: { ...v[c], opens: e.target.value } }))
            }
          />
          <Field
            label="Fecha no dia"
            inputMode="numeric"
            disabled={!canEdit}
            value={values[c].closes}
            onChange={(e) =>
              setValues((v) => ({ ...v, [c]: { ...v[c], closes: e.target.value } }))
            }
            help="31 vale como o último dia do mês."
          />
          <Field
            label="Pago no dia"
            inputMode="numeric"
            disabled={!canEdit}
            value={values[c].pay}
            onChange={(e) =>
              setValues((v) => ({ ...v, [c]: { ...v[c], pay: e.target.value } }))
            }
            help="No prazo, neste dia do mesmo mês; atrasada, no do mês seguinte. Sem ele, a nota não é enviada."
          />
        </fieldset>
      ))}
      {!canEdit && (
        <NotProvided>
          Você pode ver a janela, mas quem a altera tem outra permissão.
        </NotProvided>
      )}
      {error && <Alert tone="erro">{error}</Alert>}
      <ModalActions>
        <Button variant="contorno" onClick={onCancel}>
          {canEdit ? 'Cancelar' : 'Fechar'}
        </Button>
        {canEdit && (
          <Button type="submit" loading={busy}>
            Salvar
          </Button>
        )}
      </ModalActions>
    </form>
  );
}

function windowLabel(d: MyPaymentDetail): string {
  const w = d.window;
  if (w.estado === 'sem_janela') return 'ainda não definida nesta base';
  const range = `${formatDate(w.opensOn)} a ${formatDate(w.closesOn)}`;
  switch (w.estado) {
    case 'antes':
      return `abre em ${formatDate(w.opensOn)} (${range})`;
    case 'aberta':
      return `aberta — ${range}`;
    case 'encerrada':
      return `encerrada — era ${range}; o envio agora entra como atrasado`;
  }
}

/** `2026-09` → `09/2026`. */
function monthLabel(period: string): string {
  return `${period.slice(5, 7)}/${period.slice(0, 4)}`;
}
